import React from 'react';
import ListTable from '../shared/ListTable';
import Timestamp from '../shared/Timestamp';
import { assessmentStatusLabel, formatDuration } from '../../../models/AssessmentsModel';
import { ArchivedTip } from '../Library/LibraryDocumentMenu';
import AssessmentStatusChip from './AssessmentStatusChip';
import AssessmentMenu from './AssessmentMenu';

// The archived tooltip comes from the row's data-tip, as in the Library list.
const archivedRowProps = (a) => (a.archived
  ? { className: 'is-archived', 'data-tip': 'Archived', 'data-tip-note': 'Unarchive to use it' }
  : {});

/**
 * The list view (PRD-1703): the handoff's Assessments table on the shared List
 * table. Name, Scenario, File, Duration, Status, Uploaded and the row menu,
 * following the Library's order and widths for the columns both carry. Name is
 * pinned; every column with values sorts from its header. A row opens the
 * assessment's detail (`onOpen`) and is named "name, scenario, status".
 */
function AssessmentsList({ assessments, scenarioName, actions, onOpen, sort, onSortChange, resetKey, focusId }) {
  const columns = [
    {
      key: 'name',
      title: 'Name',
      width: '240px',
      sort: 'text',
      value: (a) => a.name,
      render: (a) => <span data-asm-focus={a.id} tabIndex={-1} className="library-cell library-cell--strong list-table-cut">{a.name}</span>,
    },
    {
      key: 'scenario',
      title: 'Scenario',
      width: 'minmax(180px, 1fr)',
      sort: 'text',
      value: (a) => scenarioName(a.scenarioId),
      render: (a) => <span className="library-cell list-table-cut">{scenarioName(a.scenarioId)}</span>,
    },
    {
      key: 'file',
      title: 'File',
      width: 'minmax(180px, 1fr)',
      sort: 'text',
      value: (a) => a.fileName,
      render: (a) => <span className="library-cell list-table-cut">{a.fileName}</span>,
    },
    {
      key: 'duration',
      title: 'Duration',
      width: '96px',
      sort: 'number',
      value: (a) => a.durationSec || null,
      render: (a) => <span className="library-cell">{formatDuration(a.durationSec)}</span>,
    },
    { key: 'status', title: 'Status', width: '132px', sort: 'text', value: assessmentStatusLabel, render: (a) => <AssessmentStatusChip assessment={a} /> },
    {
      key: 'uploaded',
      title: 'Uploaded',
      width: '120px',
      sort: 'date',
      value: (a) => Date.parse(a.uploadedAt) || null,
      render: (a) => <span className="library-cell"><Timestamp value={a.uploadedAt} /></span>,
    },
    {
      key: 'actions',
      title: <span className="library-sr-only">Actions</span>,
      label: 'Actions',
      width: '48px',
      cellClassName: 'library-cell--menu',
      render: (a) => (
        <>
          <AssessmentMenu assessment={a} actions={actions} />
          <ArchivedTip doc={a} />
        </>
      ),
    },
  ];
  return (
    <div className="library-panel">
      <ListTable
        label="Assessments"
        columns={columns}
        rows={assessments}
        rowKey={(a) => a.id}
        rowProps={archivedRowProps}
        onRowClick={(a) => onOpen(a, document.activeElement)}
        rowLabel={(a) => `${a.name}, ${scenarioName(a.scenarioId)}, ${assessmentStatusLabel(a)}`}
        sort={sort}
        onSortChange={onSortChange}
        resetKey={resetKey}
        focusRowKey={focusId}
      />
    </div>
  );
}

export default AssessmentsList;
